import useNaverADsScript, { NaverADsType } from "@repo/hooks/lib/useNaverADsScript";
import useTracking from "@repo/shopify/hooks/useTracking";
import type { IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import parse from "html-react-parser";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ICustomerState } from "yeseyesee/src/reducers/customer";

import Accordion from "@atoms/Accordion";
import Button from "@atoms/Button";
import CompareAtPrice from "@atoms/CompareAtPrice";
import Select from "@atoms/Select";
import SizeTable from "@atoms/SizeTable";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import useDropDate from "@hooks/utilsHooks/useDropDate";

import { URLS } from "@const/urls";

import { PRODUCT_TAG, showArchiveSalePrice } from "@utils/archive-sale";
import { NOTIFICATIONS } from "@utils/notifications";
import type { TProduct } from "@utils/product";
import { getAvailableSize, getProductLaunch } from "@utils/product";

import { fetchWishlist } from "@api/lamdaApi";

const DEFAULT_OPTION = { value: -1, name: "Select Size" };

export default function ProductDetail({
  product,
  size,
  isSaleShutDown = true,
}: {
  product: TProduct;
  size: { value: string; name: string }[];
  isSaleShutDown?: boolean;
}) {
  const navigate = useNavigate();
  const { customer, fetchUser, accessToken } = useUser();
  const { cart, addCartLine, addCartLoading, removeCartLineBase } = useCart();
  const { openLoginModal, openToast, openModal, closeModal, setModalProgress } = useLayout();
  const { t } = useTranslation();
  const { tryAppendNaverADsScript } = useNaverADsScript();

  const options = useMemo(() => getAvailableSize(product), [product]);
  const launch = useMemo(() => getProductLaunch(product), [product]);
  const dropDate = useDropDate({ ...launch, isDetail: true });

  const [selectedVariant, setSelectedVariant] = useState<{ value: number | string; name: string }>(DEFAULT_OPTION);
  const [draw, setDraw] = useState(false);
  const [wishListLoading, setWishListLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const [activeAccordionIdx, setActiveAccordionIdx] = useState(0);

  // userWishList에 상품이 있으면 wishlist 아이콘 활성화
  const wishList = customer ? customer.wishlist?.references.edges || [] : [];
  const isWishItem = wishList.some((wish) => wish.node.id === product.id) || false;

  const isOfflineOnly = product.tags.includes("Offline Only");

  const { gtmAddToCart } = useTracking();

  useEffect(() => {
    setSelectedVariant(DEFAULT_OPTION);

    if (product.tags.every((tag) => !tag.includes(PRODUCT_TAG))) {
      navigate(URLS.HOME);
    }

    if (product.tags.includes("Draw") && !product.tags.includes("OnSale")) {
      setDraw(true);
    }
  }, [product]);

  useEffect(() => {
    const quantityLimitProduct = parseInt(product?.quantityLimit?.value || "-1");

    // 1. 상품에 구매 수량제한이 존재하는 경우
    if (quantityLimitProduct > 0) {
      // 카트에 해당 상품이 담겨있는지 확인
      const cartLineItems = cart.lines.edges.filter(({ node }) => product?.id === node.merchandise.product.id);
      // 카트에 담긴 상품의 수량
      const cartLineItemCount = cartLineItems.reduce((acc, curr) => acc + curr.node.quantity, 0);
      // 구매 내역에 있는지 확인
      const itemInOrderHistory = customer?.orders.edges
        // exclude_limit이 true 이거나 주문 취소 했을 경우는 제외
        .filter(({ node }: { node: IOrdersEdgesNode }) => node?.exclude_limit?.value !== "true" && !node.cancelReason)
        .filter(({ node }) => node.isfamilysale)
        .map(({ node }) => node.lineItems.edges)
        .flat()
        .filter(({ node }) => node.variant?.product.id === product?.id);

      // 해당 상품을 구매한 개수
      const totalOrderedProductCount = itemInOrderHistory.reduce((total, { node }) => {
        return total + (node.quantity || 0); // currentQuantity가 0이거나 undefined일 경우를 대비해 0으로 처리
      }, 0);

      /**
       * 1. 카트에 담긴 상품 수량이 quantityLimitProduct을 초과하는 경우
       * 2. 구매 내역을 합친 수량이 quantityLimitProduct을 초과하는 경우
       * 3. 카트에 담긴 상품 수량 + 구매 내역을 합친 수량이 quantityLimitProduct을을 초과하는 경우
       */
      if (
        cartLineItemCount >= quantityLimitProduct ||
        totalOrderedProductCount >= quantityLimitProduct ||
        cartLineItemCount + totalOrderedProductCount >= quantityLimitProduct
      ) {
        setAvailable(false);
        return;
      }
    }

    // 2. 현재 고른 variant가 카트에 이미 담겨있는지 확인하고 이미 담겨있다면 구매 가능한 재고와 이미 담겨있는 재고를 비교
    if (selectedVariant.value !== DEFAULT_OPTION.value) {
      // 현재 고른 variant가 카트에 이미 담겨있는지 확인
      const variantLine = cart.lines.edges.find(({ node }) => node.merchandise.id === selectedVariant.value)?.node;
      // 이미 담겨있으면 구매 가능한 재고와 이미 담겨있는 재고를 비교
      if (variantLine && (variantLine.quantity ?? 0) >= (variantLine.merchandise.quantityAvailable ?? 0)) {
        // 구매할 수 있는 개수 이상으로 담겨있으면 available state를 false로 바꾼다.
        setAvailable(false);
        return;
      }
    }

    setAvailable(true);
  }, [product, customer, cart, selectedVariant]);

  /** 상품 option 변경 */
  const handleProductOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value, selectedIndex } = event.target;
    const isDefault = value === String(DEFAULT_OPTION.value);
    setSelectedVariant(isDefault ? DEFAULT_OPTION : { value, name: options[selectedIndex - 1]?.name || "" });
  };

  /** 상품 카트 담기 */
  const handleAddToCart = async () => {
    // addCart 하는 동안
    if (addCartLoading) return;

    if (!available) return;
    if (selectedVariant.value === DEFAULT_OPTION.value) {
      openToast(NOTIFICATIONS.WARN_SELECT_SIZE);
      return;
    }

    // 프리오더 상품을 담을 때 혹은 프리오더 상품이 카트에 있을 경우
    if (
      cart.lines.edges.length &&
      cart.lines.edges.some(({ node }) => node.merchandise.product.tags.includes("Preorder"))
    ) {
      openModal({
        title: "Preorder Notice",
        description:
          "프리오더 상품 주문은 단건으로만 가능합니다.\n해당상품을 카트에 담을 경우 기존 카트의 프리오더 상품은 삭제됩니다.",
        cancelButton: {
          label: "Cancel",
          onClick: () => {
            closeModal();
          },
        },
        confirmButton: {
          label: "Okay",
          onClick: () => {
            setModalProgress(true);
            // 현재 카트 상품 지우기
            void removeCartLineBase({
              variables: { cartId: cart.id, lineIds: cart.lines.edges.map(({ node }) => node.id) },
            }).then(() => {
              void addCartLine({
                variables: { cartId: cart.id, lines: [{ merchandiseId: selectedVariant.value, quantity: 1 }] },
              }).then(() => {
                closeModal();
              });
            });

            tryAppendNaverADsScript({ type: NaverADsType.Cart, value: "0" });
          },
        },
      });

      return;
    }

    await addCartLine({
      variables: { cartId: cart.id, lines: [{ merchandiseId: selectedVariant.value, quantity: 1 }] },
    });

    const variant = product.variants.edges.find(({ node }) => node.id === selectedVariant.value)?.node;

    if (variant) {
      gtmAddToCart(variant);
    }

    tryAppendNaverADsScript({ type: NaverADsType.Cart, value: "0" });
  };

  /** 상품 위시리스트 추가,삭제 */
  const handleWishList = async (
    _accessToken?: string,
    fetchedCustomer?: ICustomerState["customer"],
    afterLogin = false,
  ) => {
    const _customer = fetchedCustomer ?? customer;

    if (wishListLoading) return;

    if (!_customer.id) {
      openLoginModal({
        redirectUrl: location.pathname,
        // redirectUrl: "",
        callback: (callbackAccessToken?: string, callbackCustomer?: ICustomerState["customer"]) => {
          void handleWishList(callbackAccessToken, callbackCustomer, true);
        },
      });
      return;
    }

    try {
      setWishListLoading(true);
      const newWishlist: string[] = [];

      // 최초로 위시리스트 추가하면 wishlist가 null임
      if (_customer.wishlist) {
        const isWishList = _customer.wishlist.references.edges.some((wish) => wish.node.id === product.id) || false;
        if (afterLogin && isWishList) return; // 로그인 후 위시리스트가 있으면 아무 동작 하지 않음

        const wishlist = isWishItem
          ? _customer.wishlist.references.edges
              .filter(({ node }) => node.id !== product.id)
              .reduce((acc: string[], wishItem) => [...acc, wishItem.node.id], [])
          : _customer.wishlist.references.edges.map(({ node }) => node.id).concat([product.id]);

        newWishlist.push(...wishlist);
      } else {
        newWishlist.push(product.id);
      }

      const response: { updated_wishlist: string } | undefined = await fetchWishlist({
        isRemove: isWishItem,
        wishlist: newWishlist,
        customerAccessToken: _accessToken ?? accessToken,
        customerId: _customer.id,
      });
      if (response) {
        await fetchUser({ variables: { customerAccessToken: _accessToken ?? accessToken } });
        openToast(isWishItem ? NOTIFICATIONS.SUCCESS_DELETE_WISH_ITEM : NOTIFICATIONS.SUCCESS_ADD_WISH_ITEM);
      }
    } catch (e) {
      console.log("error", e);
    } finally {
      setWishListLoading(false);
    }
  };

  // description
  const tableRows = product.variants.edges.map(({ node }) => node.selectedOptions);
  // FIXME: type 정의 필요.
  const chart: string[] = JSON.parse(product.sizeChart?.value || "[]");
  const sizes = chart.map((entry: string) => entry.split("/"));
  const titles: string[] = sizes.map((size: string[]) => size[0]);
  const datas: string[][] = sizes.map((size: string[]) => size.slice(1));

  const compareAtPrice = parseInt(product.variants.edges[0].node.compareAtPrice?.amount ?? "0");
  const originalPrice = parseInt(product.variants.edges[0].node.price.amount);
  const familySalePrice = showArchiveSalePrice({ orignPrice: originalPrice, compareAtPrice, tags: product.tags });
  const salePrice = isSaleShutDown ? originalPrice : familySalePrice;

  return (
    <div className="pc:px-[10%] pc:py-[6%] relative">
      <div className="flex justify-between">
        <h2 className="font-tune block max-w-8/10 text-3xl leading-tight">{parse(product.vendor)}</h2>
      </div>

      <h2 className="font-tune mb-5 block text-3xl leading-tight">{parse(product.title)}</h2>
      {!product.title.includes("Draft Order") && (
        <CompareAtPrice
          className="pc:text-base mb-5 text-xl"
          originalPrice={parseInt(product.variants.edges[0].node.compareAtPrice?.amount ?? "0")}
          salePrice={salePrice}
        />
      )}

      {isOfflineOnly ? (
        <>
          <Button className="mb-5" disabled styleType="secondary">
            Offline Only
          </Button>
          <p className="mb-5 font-normal whitespace-pre-wrap">
            해당 상품은 오프라인 전용 판매 제품으로 성수 스토어에서 구매 가능합니다.
          </p>
        </>
      ) : (
        <>
          {dropDate.dropStatus === "Upcoming" && (
            <Button className="mb-5" disabled styleType="secondary">
              Release Soon
            </Button>
          )}

          {dropDate.dropStatus !== "Upcoming" && !product.availableForSale && (
            <Button className="mb-5" disabled styleType="secondary">
              SOLD OUT
            </Button>
          )}

          {dropDate.dropStatus !== "Upcoming" && product.availableForSale ? (
            <>
              {!draw && (
                <>
                  <div className="mb-5 flex gap-2">
                    <Select
                      id="sizeSelector"
                      onChange={handleProductOption}
                      options={size}
                      // options={options}
                      placeholder={product.title.includes("Draft Order") ? "Select Price" : "Select Size"}
                      selected={selectedVariant}
                    />
                    <Button
                      disabled={!available || isSaleShutDown}
                      onClick={() => {
                        handleAddToCart().catch(() => 0);
                      }}
                      styleType="primary"
                    >
                      Add to Cart
                    </Button>
                  </div>
                  {!available && <div className="mb-5 text-red-400">*{t("toast.product.error.exceed_qty")}</div>}
                </>
              )}
            </>
          ) : null}
        </>
      )}

      {product.notice?.value ? (
        <p className="mb-5 font-normal whitespace-pre-wrap text-red-400">{product.notice.value}</p>
      ) : null}

      <ul className="flex flex-col items-start gap-2.5" data-accordion="collapse">
        <li>
          {(product.seoungsu_stock?.value === "true" || product.tags.includes("No Membership")) && (
            <p className="mb-5 font-normal whitespace-pre-wrap">
              {product.seoungsu_stock?.value === "true" && (
                <>
                  해당 상품의 일부 재고는 성수 스토어에서 출고되며, 주문 과정에서 재고가 없을 경우 환불 처리될 수
                  있습니다.
                  <br />
                </>
              )}
            </p>
          )}
        </li>
        <li>
          <Accordion
            description={`${String(product.descriptionHtml)}\n${product.additionalDescription?.value || ""}`}
            handleOpen={() => {
              setActiveAccordionIdx(0);
            }}
            isOpen={activeAccordionIdx === 0}
            title={t("products.product.desc.title")}
          />
        </li>

        {Boolean(chart.length) && (
          <li>
            <Accordion
              handleOpen={() => {
                setActiveAccordionIdx(1);
              }}
              isOpen={activeAccordionIdx === 1}
              title={t("products.product.sizeChart.title")}
            >
              <div className="flex flex-col gap-2.5 pt-2.5">
                <span>{t("products.product.sizeChart.alert")}</span>
                <SizeTable data={datas} options={tableRows} title={titles} />
              </div>
            </Accordion>
          </li>
        )}
        <li>
          <Accordion
            alertDescription="아카이브 세일 주문은 ‘배송 준비 중’ 상태 이후 주문 취소, 교환, 반품이 불가하오니 신중한 구매 부탁드립니다."
            description={t("products.product.rtnExchDelivery.description")}
            handleOpen={() => {
              setActiveAccordionIdx(2);
            }}
            isOpen={activeAccordionIdx === 2}
            title={t("products.product.rtnExchDelivery.title")}
          />
        </li>
      </ul>
    </div>
  );
}
